import classNames from 'classnames';
import React, { forwardRef, useCallback, useState } from 'react';
import { Page } from 'react-pdf';

import './PageThumbnail.less';

interface PageThumbnailProps {
  maxWidth: number;
  maxHeight: number;
  page: number;
  active: boolean;
  /** Height / width of a typical page, until this page's size is known */
  defaultAspectRatio: number;
  onClick: (page: number) => void;
}

function fitPage(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
) {
  const scale = Math.min(maxWidth / width, maxHeight / height);
  return { width: width * scale, height: height * scale };
}

const PageThumbnail = forwardRef<HTMLDivElement, PageThumbnailProps>(
  function PageThumbnail(
    { maxWidth, maxHeight, page, active, defaultAspectRatio, onClick },
    ref
  ) {
    // Sized from the typical page right away, so it does not resize once
    // loaded (unless this page has other proportions)
    const [pageSize, setPageSize] = useState(() =>
      fitPage(1, defaultAspectRatio, maxWidth, maxHeight)
    );

    const onLoadSuccess = useCallback(
      (pdfPage: {
        rotate: number;
        originalWidth: number;
        originalHeight: number;
      }) => {
        let { originalWidth, originalHeight } = pdfPage;
        if (pdfPage.rotate % 180 !== 0) {
          const tmp = originalWidth;
          originalWidth = originalHeight;
          originalHeight = tmp;
        }
        setPageSize(
          fitPage(originalWidth, originalHeight, maxWidth, maxHeight)
        );
      },
      [maxWidth, maxHeight]
    );

    return (
      <div className="page-thumbnail" ref={ref}>
        <div
          className={classNames({
            'page-thumbnail-item': true,
            active
          })}
        >
          <Page
            width={
              pageSize.width > pageSize.height ? pageSize.width : undefined
            }
            height={
              pageSize.height > pageSize.width ? pageSize.height : undefined
            }
            pageNumber={page}
            className="page-thumbnail-page"
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onLoadSuccess={onLoadSuccess}
            // A blank page while loading, instead of a spinner
            loading={
              <div
                className="page-thumbnail-placeholder"
                style={{ width: pageSize.width, height: pageSize.height }}
              />
            }
          />
          <div className="page-thumbnail-link" onClick={() => onClick(page)} />
          <div className="page-thumbnail-number">{page}</div>
        </div>
      </div>
    );
  }
);

export default PageThumbnail;
